import React, { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, DollarSign, Wallet, TrendingUp, Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const Stat = ({ icon: Icon, label, value, accent = false, testid }) => (
  <div className={`glass rounded-xl p-5 ${accent ? "border-[#ff7a00]/40" : ""}`} data-testid={testid}>
    <div className="w-10 h-10 rounded-lg bg-[#ff7a00]/10 border border-[#ff7a00]/30 flex items-center justify-center text-[#ff7a00]">
      <Icon size={18} />
    </div>
    <div className="mt-4 font-display text-2xl font-bold">{value}</div>
    <div className="text-xs text-[#8a8a8a] uppercase tracking-wider mt-1">{label}</div>
  </div>
);

export const AdminAffiliate = () => {
  const [overview, setOverview] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [deciding, setDeciding] = useState(null);
  const [form, setForm] = useState({ status: "approved", tx_id: "", admin_note: "" });
  const [busy, setBusy] = useState(false);

  const load = () => Promise.all([
    api.get("/admin/affiliate/overview").then((r) => setOverview(r.data)),
    api.get("/admin/affiliate/withdrawals").then((r) => setWithdrawals(r.data || [])),
    api.get("/admin/affiliate/commissions").then((r) => setCommissions(r.data || [])),
  ]);
  useEffect(() => { load().catch(() => {}); }, []);

  const decide = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch(`/admin/affiliate/withdrawals/${deciding.id}`, form);
      toast.success(`Withdrawal ${form.status}. Partner notified.`);
      setDeciding(null);
      setForm({ status: "approved", tx_id: "", admin_note: "" });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-affiliate-page">
      <div>
        <div className="text-[#ff7a00] text-xs tracking-[0.3em] uppercase mb-1">Affiliate Program</div>
        <h1 className="font-display text-3xl font-bold">Overview & Withdrawals</h1>
      </div>

      {overview && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <Stat icon={Users} label="Affiliates" value={overview.total_affiliates} testid="aff-stat-affiliates" />
          <Stat icon={TrendingUp} label="Referrals" value={overview.total_referrals} testid="aff-stat-referrals" />
          <Stat icon={DollarSign} label="Commissions" value={`$${overview.total_commissions.toLocaleString()}`} accent testid="aff-stat-commissions" />
          <Stat icon={Wallet} label="Paid Out" value={`$${overview.total_paid_out.toLocaleString()}`} testid="aff-stat-paid" />
          <Stat icon={Send} label="Pending" value={overview.pending_withdrawals} testid="aff-stat-pending" />
        </div>
      )}

      <div className="glass rounded-xl overflow-x-auto" data-testid="admin-withdrawals-table">
        <div className="p-5 border-b border-[#ff7a00]/15 font-display text-lg font-semibold">Withdrawal Requests</div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] tracking-widest text-[#8a8a8a] uppercase border-b border-[#ff7a00]/15">
              <th className="text-left p-4">Date</th>
              <th className="text-left p-4">Affiliate</th>
              <th className="text-right p-4">Amount</th>
              <th className="text-left p-4">Wallet (USDT BEP20)</th>
              <th className="text-left p-4">Status</th>
              <th className="text-right p-4">Action</th>
            </tr>
          </thead>
          <tbody>
            {withdrawals.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-[#8a8a8a]">No withdrawal requests yet.</td></tr>}
            {withdrawals.map((w) => (
              <tr key={w.id} className="border-b border-[#ff7a00]/5" data-testid={`admin-wd-${w.id}`}>
                <td className="p-4 text-[#8a8a8a]">{new Date(w.created_at).toLocaleDateString()}</td>
                <td className="p-4 text-[#cfcfcf]">{w.affiliate_user_email}</td>
                <td className="p-4 text-right font-display font-bold text-[#ff7a00]">${w.amount.toLocaleString()}</td>
                <td className="p-4 font-mono text-xs text-[#8a8a8a] max-w-[220px] truncate">{w.wallet_address}</td>
                <td className="p-4">
                  <Badge className={`uppercase text-[10px] tracking-widest ${
                    w.status === "paid" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" :
                    w.status === "approved" ? "bg-[#ff7a00]/15 text-[#ff7a00] border-[#ff7a00]/30" :
                    w.status === "rejected" ? "bg-red-500/15 text-red-400 border-red-500/30" :
                    "bg-yellow-500/15 text-yellow-400 border-yellow-500/30"
                  }`}>{w.status}</Badge>
                </td>
                <td className="p-4 text-right">
                  {(w.status === "pending" || w.status === "approved") ? (
                    <Button size="sm" variant="ghost" onClick={() => setDeciding(w)} className="text-[#ff9a2f] hover:text-[#ff7a00]" data-testid={`wd-decide-${w.id}`}>Process</Button>
                  ) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="glass rounded-xl overflow-x-auto" data-testid="admin-commissions-table">
        <div className="p-5 border-b border-[#ff7a00]/15 font-display text-lg font-semibold">All Commissions ({commissions.length})</div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] tracking-widest text-[#8a8a8a] uppercase border-b border-[#ff7a00]/15">
              <th className="text-left p-4">Date</th>
              <th className="text-left p-4">Referred</th>
              <th className="text-left p-4">Challenge</th>
              <th className="text-right p-4">Order</th>
              <th className="text-right p-4">Commission</th>
            </tr>
          </thead>
          <tbody>
            {commissions.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-[#8a8a8a]">No commissions recorded yet.</td></tr>}
            {commissions.slice(0, 30).map((c) => (
              <tr key={c.id} className="border-b border-[#ff7a00]/5">
                <td className="p-4 text-[#8a8a8a]">{new Date(c.created_at).toLocaleDateString()}</td>
                <td className="p-4 text-[#cfcfcf]">{c.referred_user_email || c.referred_user_id.slice(0, 8)}</td>
                <td className="p-4 text-[#f5f5f5]">{c.challenge_name || "—"}</td>
                <td className="p-4 text-right text-[#cfcfcf]">${c.order_amount_usd}</td>
                <td className="p-4 text-right font-display font-bold text-[#ff7a00]">+${c.commission_usd.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!deciding} onOpenChange={(o) => !o && setDeciding(null)}>
        <DialogContent className="bg-[#0a0a0a] border-[#ff7a00]/30 text-[#f5f5f5]" data-testid="wd-decision-dialog">
          {deciding && (
            <>
              <DialogHeader><DialogTitle className="font-display">Process Withdrawal — ${deciding.amount.toLocaleString()}</DialogTitle></DialogHeader>
              <form onSubmit={decide} className="space-y-4">
                <div className="glass rounded-lg p-3 text-xs">
                  <div className="text-[#8a8a8a]">Affiliate: <span className="text-[#f5f5f5]">{deciding.affiliate_user_email}</span></div>
                  <div className="text-[#8a8a8a] mt-1">Wallet: <span className="text-[#ff9a2f] font-mono break-all">{deciding.wallet_address}</span></div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#cfcfcf]">Decision</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger className="bg-[#050505] border-[#ff7a00]/20 h-11 mt-2"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0a0a0a] border-[#ff7a00]/30 text-[#f5f5f5]">
                      <SelectItem value="approved" className="text-[#f5f5f5]">Approve</SelectItem>
                      <SelectItem value="paid" className="text-[#f5f5f5]">Mark as Paid</SelectItem>
                      <SelectItem value="rejected" className="text-[#f5f5f5]">Reject</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.status === "paid" && (
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[#cfcfcf]">Transaction Hash</Label>
                    <Input value={form.tx_id} onChange={(e) => setForm({ ...form, tx_id: e.target.value })} className="bg-[#050505] border-[#ff7a00]/20 h-11 mt-2 font-mono text-xs" data-testid="wd-tx-hash" />
                  </div>
                )}
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#cfcfcf]">Admin Note (optional)</Label>
                  <Textarea value={form.admin_note} onChange={(e) => setForm({ ...form, admin_note: e.target.value })} className="bg-[#050505] border-[#ff7a00]/20 mt-2" data-testid="wd-admin-note" />
                </div>
                <Button type="submit" disabled={busy} className="w-full h-11 bg-[#ff7a00] hover:bg-[#ff9a2f] text-[#050505] font-semibold" data-testid="wd-decision-submit">
                  {busy ? "Processing…" : "Confirm"}
                </Button>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
